/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { LinearProgress } from '@mui/material';
import LegacyGrid from './legacy/LegacyGrid';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import { Theme } from '@mui/material';
import { withStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import 'jspdf-autotable';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Category, CategoryType } from '../common/Categories';
import { actionCreators } from '../store/ViewDownloads';
import './DataTable.css';
import './styles.css';

const styles = (theme: Theme | any) => ({
    currency: {
        fontWeight: (theme && theme.typography && theme.typography.fontWeightMedium) ? theme.typography.fontWeightMedium : 500
    },
    numericInput: {
        width: '100%'
    },
    avatar: {
        width: '12px',
        height: '12px',
        display: 'inline-block',
        marginLeft: '10px',
        marginBottom: '-2px',
        marginRight: '2px'
    },
    blackAvatar: {
        backgroundColor: '#000000'
    },
    redAvatar: {
        backgroundColor: '#a51d25'
    },
    orangeAvatar: {
        backgroundColor: '#ff8b00'
    },
    greyAvatar: {
        backgroundColor: '#bfbfc6'
    },
    agingText: {
        fontFamily: 'Roboto',
        fontSize: '12px'
    }
});

interface Props {
    selectedCategory?: Category; // optional to ease connected usage
    gridData?: any;
    isLoading: boolean;
    classes?: any;
    error: any;
    selectedDownloadIds: any;
    selectDownload: Function;
    unselectDownload: Function;
    updatedSelectedId: Function;
}

class DownloadDataTable extends React.PureComponent<Props, any> {
    state = {
        rows: undefined,
        loading: false,
        selected: [] as any,
        expanded: [] as any,
        selectedDownload: [] as any,
        columns: [],
        isSelectOnlyOne: false,
        isCheckBoxEnabled: false,
    };

    componentDidMount() {
        if (this.props.selectedCategory !== undefined) {
            this.setState({
                columns: this.props.selectedCategory.columns,
                isSelectOnlyOne: this.props.selectedCategory.isSelectOneOnly,
                isCheckBoxEnabled: this.props.selectedCategory.isCheckBoxEnabled
            });
        }
    }

    getTableRow = (rowItem: any, isSelected: boolean, isExpanded: boolean, selectedCategoryType: CategoryType) => {
        if (isExpanded) {
            return (
                <TableRow
                    className={`dtable-row ${isSelected ? 'selected' : ''} ${rowItem['status'] ? '' : 'inActive'}`}
                    key={rowItem.downloadsId}
                    hover
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={-1}
                    selected={isSelected}
                >
                    <TableCell id="expandedrow" colSpan={12}>
                        <LegacyGrid container>
                            <LegacyGrid className="downloadsCheckboxalign">
                                {
                                    this.state.isCheckBoxEnabled === true &&
                                    <Checkbox checked={isSelected} onClick={event => this.handleClick(event, rowItem.downloadsId, rowItem)} />
                                }
                            </LegacyGrid>
                            <LegacyGrid item xs={5} onClick={event => this.handleClick(event, rowItem.downloadsId, rowItem)}>
                                <LegacyGrid container className="downloadsInformationContent">
                                    <LegacyGrid item xs={4} className="downloadsExpandHeaderalign">File Name</LegacyGrid>
                                    <LegacyGrid item xs={8} className="downloadsTextalign">{rowItem['fileName']}</LegacyGrid>
                                </LegacyGrid>
                                <LegacyGrid container className="downloadsInformationContent">
                                    <LegacyGrid item xs={4} className="downloadsExpandHeaderalign">Revision</LegacyGrid>
                                    <LegacyGrid item xs={8} className="downloadsTextalign">{rowItem['revision']}</LegacyGrid>
                                </LegacyGrid>
                                <LegacyGrid container className="downloadsInformationContent">
                                    <LegacyGrid item xs={4} className="downloadsExpandHeaderalign">Release Date</LegacyGrid>
                                    <LegacyGrid item xs={8} className="downloadsTextalign">{new Date(rowItem['releaseDate']).toLocaleDateString()}</LegacyGrid>
                                </LegacyGrid>
                                <LegacyGrid container className="downloadsInformationContent">
                                    <LegacyGrid item xs={4} className="downloadsExpandHeaderalign">Status</LegacyGrid>
                                    <LegacyGrid item xs={8} className="downloadsTextalign">{rowItem['status'] ? 'Active' : 'Inactive'}</LegacyGrid>
                                </LegacyGrid>
                            </LegacyGrid>
                            <LegacyGrid item xs={5} onClick={event => this.handleClick(event, rowItem.downloadsId, rowItem)}>
                                <LegacyGrid container className="downloadsInformationContent">
                                    <LegacyGrid item xs={4} className="downloadsExpandHeaderalign">Description</LegacyGrid>
                                    <LegacyGrid item xs={8} className="downloadsTextalign">{rowItem['description']}</LegacyGrid>
                                </LegacyGrid>
                                <LegacyGrid container className="downloadsInformationContent">
                                    <LegacyGrid item xs={4} className="downloadsExpandHeaderalign">Firmware Type</LegacyGrid>
                                    <LegacyGrid item xs={8} className="downloadsTextalign">{rowItem['firmwareType']}</LegacyGrid>
                                </LegacyGrid>
                                <LegacyGrid container className="downloadsInformationContent">
                                    <LegacyGrid item xs={4} className="downloadsExpandHeaderalign">Introduction Date</LegacyGrid>
                                    <LegacyGrid item xs={8} className="downloadsTextalign">{rowItem['introductionDate'] != null &&
                                        rowItem['introductionDate'] !== undefined ? new Date(rowItem['introductionDate']).toLocaleDateString() : ''}</LegacyGrid>
                                </LegacyGrid>
                                <LegacyGrid container className="downloadsInformationContent">
                                    <LegacyGrid item xs={4} className="downloadsExpandHeaderalign">Model Code</LegacyGrid>
                                    <LegacyGrid item xs={8} className="downloadsTextalign">{rowItem['downloadsClassificationModelCodeMapping'].join()}</LegacyGrid>
                                </LegacyGrid>
                            </LegacyGrid>
                            <LegacyGrid className="downloadsArrow">
                                <ArrowDropUp className="editColor" style={{ cursor: 'pointer' }}
                                    onClick={event => this.handleExpandableClick(event, rowItem.downloadsId, rowItem)} />
                            </LegacyGrid>
                            <LegacyGrid className="downloadsCommentsalign"></LegacyGrid>
                            <LegacyGrid xs={10}>
                                <LegacyGrid container className="downloadsInformationContent">
                                    <LegacyGrid item xs={2} className="downloadsExpandHeaderalign">Comments</LegacyGrid>
                                    <LegacyGrid item xs={10} className="downloadsTextalign">{rowItem['comments']}</LegacyGrid>
                                </LegacyGrid>
                            </LegacyGrid>
                            <LegacyGrid className="downloadsCommentsalignAfter"></LegacyGrid>
                            <LegacyGrid className="downloadsFileInssalign"></LegacyGrid>
                            <LegacyGrid xs={10}>
                                <LegacyGrid container className="downloadsInformationContent" style={{ marginBottom: '16px' }}>
                                    <LegacyGrid item xs={2} className="downloadsExpandHeaderalign">File Instructions</LegacyGrid>
                                    <LegacyGrid item xs={10} className="downloadsTextalign">{rowItem['fileInstructions']}</LegacyGrid>
                                </LegacyGrid>
                            </LegacyGrid>
                        </LegacyGrid>
                    </TableCell>
                </TableRow>
            );
        }
        else {
            return (
                <TableRow
                    className={`dtable-row ${isSelected ? 'selected' : ''} ${rowItem['status'] ? '' : 'inActive'}`}
                    key={rowItem.downloadsId}
                    hover
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={-1}
                    selected={isSelected}
                >
                    {
                        (this.props.selectedCategory !== undefined && this.state.isCheckBoxEnabled === true) &&
                        (
                            <TableCell className="dtable-cell" padding="none" style={{ width: '24px' }}>
                                <Checkbox checked={isSelected} onClick={event => this.handleClick(event, rowItem.downloadsId, rowItem)} />
                            </TableCell>
                        )
                    }
                    {
                        this.props.selectedCategory !== undefined && this.state.columns.map((column: any, index: any) => {
                            return ((column.display === true) &&
                                <TableCell className="dtable-cell" key={column.name} id={column.name} style={{ maxWidth: column.width, textIndent: '10px' }} >
                                    <LegacyGrid container>
                                        <LegacyGrid item xs={11} onClick={event => this.handleClick(event, rowItem.downloadsId, rowItem)}>
                                            <div style={{ height: 20, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                                                className={`${rowItem['status'] ? '' : 'inActiveFont'}`}>
                                                {(column.name !== 'releaseDate') && rowItem[column.name]}
                                                {(column.name === 'releaseDate') && new Date(rowItem[column.name]).toLocaleDateString()}
                                            </div>
                                        </LegacyGrid>
                                        {(this.state.columns.length) - 1 === index &&
                                        <LegacyGrid className="downloadsCollapsedArrow" item xs={1}
                                            onClick={event => this.handleExpandableClick(event, rowItem.downloadsId, rowItem)}>
                                                <ArrowDropDown className="editColor" style={{ cursor: 'pointer' }} />
                                            </LegacyGrid>
                                        }
                                    </LegacyGrid>
                                </TableCell>
                            );
                        })
                    }
                </TableRow>
            );
        }
    };

    handleSelectAllClick = (event: any) => {
        this.props.unselectDownload();
        if (event.target.checked) {
            this.props.updatedSelectedId(this.props.gridData.map((rowItem: any) => rowItem.downloadsId));
            this.setState((state: any) => ({
                selected: this.props.gridData.map((rowItem: any) => rowItem.downloadsId)
            }));
        }
        else {
            this.props.updatedSelectedId([]);
            this.setState({ selected: [] });
        }
    };

    handleClick = (event: any, id: number, rowItem: any) => {
        let { selected } = this.state;
        const selectedIndex = selected.indexOf(id);
        if (this.state.isSelectOnlyOne && selectedIndex !== 0) {
            selected = [];
        }
        let newSelected: any = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }
        this.setState({ selected: newSelected });
        this.props.updatedSelectedId(newSelected);
        if (newSelected.length === 1) {
            const selectedRow = this.props.gridData.find(function (a: any) {
                return a.downloadsId === newSelected[0];
            });
            this.props.selectDownload(selectedRow);
        }
        else {
            this.props.unselectDownload();
        }
    };

    handleExpandableClick = (event: any, id: number, rowItem: any) => {
        let { expanded } = this.state;
        const expandedIndex = expanded.indexOf(id);
        if (this.state.isSelectOnlyOne && expanded !== 0) {
            expanded = [];
        }
        let newExpanded: any = [];

        if (expandedIndex === -1) {
            newExpanded = newExpanded.concat(expanded, id);
        } else if (expandedIndex === 0) {
            newExpanded = newExpanded.concat(expanded.slice(1));
        } else if (expandedIndex === expanded.length - 1) {
            newExpanded = newExpanded.concat(expanded.slice(0, -1));
        } else if (expandedIndex > 0) {
            newExpanded = newExpanded.concat(
                expanded.slice(0, expandedIndex),
                expanded.slice(expandedIndex + 1)
            );
        }
        this.setState({ expanded: newExpanded });
    };

    isSelected = (id: number) => {
        if ((this.props.selectedDownloadIds == null || this.props.selectedDownloadIds.length <= 0) && this.state.selected.length > 0) {
            this.setState({ selected: [] });
        }
        return this.props.selectedDownloadIds != null && this.props.selectedDownloadIds.length > 0 && this.state.selected.indexOf(id) !== -1;
    };
    isExpanded = (id: number) => this.state.expanded.indexOf(id) !== -1;

    public render(): React.ReactNode {
        const { selected, columns } = this.state;
        const { gridData, error } = this.props;

        const loading = this.props.isLoading;
        const numSelected = selected.length;
        return (
                <LegacyGrid className="viewDownloadGrid">
                <Paper>
                    <Table className="dtable">
                        <TableHead>
                            <TableRow className="dtable-row-head">
                                {(this.props.selectedCategory !== undefined && this.state.isCheckBoxEnabled === true && gridData != null && gridData.length !== 0) && (
                                    <TableCell className="dtable-cell" padding="checkbox" style={{ width: '24px' }}>
                                        <Checkbox
                                            indeterminate={
                                                numSelected > 0 &&
                                                numSelected < (gridData && gridData.length)
                                            }
                                            checked={
                                                numSelected === (gridData && gridData.length)
                                            }
                                            onChange={this.handleSelectAllClick}
                                        />
                                    </TableCell>
                                )}
                                {columns.map((column: any) => {
                                    return (
                                        <TableCell className="dtable-cell downloadsHeaderalign" key={column.name} style={{ maxWidth: column.width }}> {column.title}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                (gridData != null &&
                                    gridData.length !== 0) && error === undefined ?
                                    gridData.map((rowItem: any) => {
                                        const isSelected = this.isSelected(rowItem.downloadsId);
                                        const isExpanded = this.isExpanded(rowItem.downloadsId);
                                        return this.getTableRow(rowItem, isSelected, isExpanded, this.props.selectedCategory !== undefined ?
                                            this.props.selectedCategory.type : CategoryType.ServiceCase);
                                    }) :
                                    !this.props.isLoading && error !== undefined ?
                                        <TableRow>
                                            <TableCell id="nodownloadrecords" colSpan={12} align='center' className='no-records'>
                                                {error.code === 401 && error.message ? error.message : 'An error occurred while processing this request.'}
                                            </TableCell>
                                        </TableRow> :
                                        !this.props.isLoading && gridData != null && gridData.length === 0 ?
                                            <TableRow>
                                                <TableCell id="nodownloadrecords" colSpan={12} align='center' className='no-records'>No records found </TableCell>
                                            </TableRow> :
                                            !this.props.isLoading &&
                                            <TableRow>
                                                <TableCell id="nodownloadrecords" colSpan={12} align='center' className='no-records'>Apply filter to populate this list</TableCell>
                                            </TableRow>
                            }
                        </TableBody>
                    </Table>
                    {loading && <LinearProgress color="secondary" />}
                </Paper>
                </LegacyGrid>
        );
    }
}

const mapStateToProps = (state: any) => ({
    state: state.viewDownloads,
    isLoading: state.viewDownloads.isLoading,
    selectedDownloadIds: state.viewDownloads.selectedDownloadIds,
    error: state.dataTable.error
});

const mapDispatchtoProps = (dispatch: any) =>
    bindActionCreators(actionCreators, dispatch);

const StyledDownloadDataTable = withStyles(styles, { withTheme: true })(DownloadDataTable as any);
export default connect(
    mapStateToProps,
    mapDispatchtoProps
)(StyledDownloadDataTable) as React.ComponentType<Partial<Props>>;
